import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { Permission } from "../schemas/permissionSchema";
import {
  permissionService,
  PermissionFilters,
  PermissionsResponse,
} from "../api/services/permissionService";

export const usePermissions = (
  filters: PermissionFilters = {},
  options?: { enabled?: boolean },
) => {
  const queryClient = useQueryClient();

  const {
    data: permissions,
    isLoading: isPermissionsLoading,
    refetch,
  } = useQuery<PermissionsResponse, Error>({
    queryKey: ["permissions", filters],
    queryFn: () => permissionService.getAll(filters),
    enabled: options?.enabled !== false,
  });

  const createPermissionMutation = useMutation({
    mutationFn: permissionService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["permissions"] });
      toast.success("Permission created successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create permission");
    },
  });

  const deletePermissionMutation = useMutation({
    mutationFn: permissionService.remove,
    onSuccess: (_data, ids) => {
      queryClient.invalidateQueries({ queryKey: ["permissions"] });
      if (Array.isArray(ids) && ids.length > 1) {
        toast.success(`${ids.length} items deleted successfully`);
        return;
      }
      toast.success("Permission deleted successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete permission");
    },
  });

  return {
    permissions,
    isLoading:
      isPermissionsLoading ||
      createPermissionMutation.isPending ||
      deletePermissionMutation.isPending,
    refetchPermissions: refetch,
    createPermission: createPermissionMutation.mutate,
    deletePermissions: deletePermissionMutation.mutate,
  };
};
